import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Storage } from '@ionic/storage-angular';
import moment from 'moment';
import { AuthService } from '../../app/services/auth.service';
import { environment } from 'src/environments/environment';
import { ApiService } from '../provider/api.service';
import { endpoints } from 'src/app/constant/endpoints';
import { catchError, first, map, switchMap, filter, shareReplay, distinctUntilChanged, tap } from 'rxjs/operators';
import { Observable, of, throwError, BehaviorSubject, forkJoin } from 'rxjs';
import { defer } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class DataService {
  customer: any | null;
  authToken: string | null;
  today: string = moment().format('YYYY-MM-DD');

  private cachedObservables = new Map<string, Observable<any>>();
  private _currentUser$ = new BehaviorSubject<any | null>(null);
  private cachedData = new Map<string, any>(); // <-- stores latest values synchronously

  constructor(private authService: AuthService, private apiService: ApiService) {
    this.customer = null;
    this.authToken = null;
    this.initializeServiceOnStartup();
    this.listenToAuthChanges();
  }

  private async initializeServiceOnStartup(): Promise<void> {
    // await this.initializeStorage();
    this.preloadEssentialData().subscribe();
  }



  private listenToAuthChanges(): void {
    this.authService.currentUser$
      .pipe(
        distinctUntilChanged((prev, curr) => prev?.id === curr?.id),
        tap((user) => {
          this.customer = user;
          this.authToken = user?.session_token || null;
          this._currentUser$.next(user);

          if (user && user.id) {
            // Load user-specific data
            this.loadUserData().subscribe({
              next: (data) => console.log(data,"freshData"),
              error: (err) => console.error('Error', err),
            });
          } else {
            // Clear on logout
            this.clearUserSpecificData();
            console.log('Clean Cache');
          }
        }),
        catchError((err) => {
          console.error('No Auth', err);
          return of(null);
        }),
      )
      .subscribe();
  }

  private fetchData<T>(url: string): Observable<T> {
    // Return cached observable if exists
    if (this.cachedObservables.has(url)) {
      return this.cachedObservables.get(url)!;
    }

    const data$ = this.apiService.get(url).pipe(
      map((response) => {
        if (response?.data != null) {
          return response.data as T;
        }
        throw new Error(`API returned no data for ${url}`);
      }),
      tap((data) => {
        // Save latest data for synchronous access
        this.cachedData.set(url, data);
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
      catchError((error) => {
        // Remove from cachedObservables on error to allow retry
        this.cachedObservables.delete(url);
        return throwError(() => new Error(`Failed to fetch data from ${url}: ${error.message}`));
      }),
    );

    // Cache the observable for future subscribers
    this.cachedObservables.set(url, data$);

    return data$;
  }

  private loadSliderData(): Observable<any[]> {
    // We now have to cast to the correct type after fetching
    return this.fetchData<any[]>(endpoints.slider);
  }
  private loadCMSPagesData(): Observable<any[]> {
    // We now have to cast to the correct type after fetching
    return this.fetchData<any[]>(endpoints.cms);
  }
  private loadConfigurationsData(): Observable<any> {
    // This correctly fetches an object, not an array
    return this.fetchData<any>(endpoints.configuration);
  }

  private loadProfileData(): Observable<any> {
    return this._currentUser$.pipe(
      filter((user) => !!user?.id),
      first(),
      switchMap((user) => this.fetchData<any>(`${endpoints.profile}?customer_id=${user.id}`)),
    );
  }

  private loadSubscriptionsData(): Observable<any[]> {
    return this.fetchData<any[]>(endpoints.subscriptions);
  }

  private loadMySubscriptionsNewData(): Observable<any[]> {
    return this._currentUser$.pipe(
      filter((user) => !!user?.id),
      first(),
      switchMap((user) => this.fetchData<any[]>(`${endpoints.mySubscriptionsnew}?customer_id=${user.id}`)),
    );
  }

  private loadSubDataData(): Observable<any> {
    return this._currentUser$.pipe(
      filter((user) => !!user?.id),
      first(),
      switchMap((user) => this.fetchData<any>(`${endpoints.subData}?customer_id=${user.id}`)),
    );
  }

  private loadCalendarData(month: string): Observable<any> {
    return this._currentUser$.pipe(
      filter((user) => !!user?.id),
      first(),
      switchMap((user) => {
        const url = `${endpoints.calendar}?customer_id=${user.id}&month=${month}`;
        return this.fetchData<any>(url);
      }),
    );
  }

  private loadAddressesData(): Observable<any[]> {
    return this._currentUser$.pipe(
      filter((user) => !!user?.id),
      first(),
      switchMap((user) => this.fetchData<any[]>(`${endpoints.addresses}?customer_id=${user.id}`)),
    );
  }

  private loadSlotData(area_id: number): Observable<any[]> {
    return this.fetchData<any[]>(`${endpoints.slots}?area_id=${area_id}`);
  }

  private loadDriverNoteData(): Observable<any[]> {
    return this._currentUser$.pipe(
      filter((user) => !!user?.id),
      first(),
      switchMap((user) => this.fetchData<any[]>(`${endpoints.driver_notes}?customer_id=${user.id}`)),
    );
  }

  private loadConsultationsData(): Observable<any[]> {
    return this._currentUser$.pipe(
      filter((user) => !!user?.id),
      first(),
      switchMap((user) => this.fetchData<any[]>(`${endpoints.consultations}?customer_id=${user.id}`)),
    );
  }
  private loadConsultantsData(date: string): Observable<any[]> {
    return this._currentUser$.pipe(
      filter((user) => !!user?.id),
      first(),
      switchMap((user) => this.fetchData<any[]>(`${endpoints.consultants}?customer_id=${user.id}&date=${date}`)),
    );
  }

  private loadMenuItemsData(): Observable<any> {
    return this.fetchData<any>(endpoints.menuItems);
  }
    private loadAreasData(): Observable<any> {
    return this.fetchData<any>(endpoints.areas);
  }

  private loadMenuPageData(): Observable<any> {
    return this._currentUser$.pipe(
      filter((user) => !!user?.id),
      first(),
      switchMap((user) => {
        let url;
          url = `${endpoints.menuSubscriptions}`;
        return this.fetchData<any>(url);
      }),
    );
  }

    private loadMenuWithDateData(date: string): Observable<any> {
    return this._currentUser$.pipe(
      filter((user) => !!user?.id),
      first(),
      switchMap((user) => {
        let url;
          url = `${endpoints.menuSubscriptions}&date=${date}`;
        return this.fetchData<any>(url);
      }),
    );
  }

  preloadEssentialData(): Observable<any> {
    return defer(() => {
      console.log('[preloadEssentialData] Observable created & subscribed');
      return forkJoin([this.loadConfigurationsData(), this.loadSubscriptionsData(), this.loadMenuItemsData(), this.loadAreasData()]).pipe(
        tap((data) => {
          // console.log('[preloadEssentialData] Data arrived & emitted:', data);
        }),
        catchError((err) => {
          console.error('[preloadEssentialData] Error:', err);
          return of(null);
        }),
      );
    });
  }

  loadUserData(): Observable<any> {
    const currentMonth = moment().format('YYYY-MM');
    return defer(() => {
      console.log('[loadUserData] Observable created & subscribed');
      return forkJoin([
        this.loadProfileData(),
        this.loadMySubscriptionsNewData(),
        this.loadSubDataData(),
        this.loadAddressesData(),
        this.loadCalendarData(currentMonth),
        this.loadConsultationsData(),
        this.loadConsultantsData(this.today),
        this.loadCMSPagesData(),
        this.loadDriverNoteData(),
      ]).pipe(
        tap((data) => {
          // console.log('[loadUserData] Data arrived & emitted:', data);
        }),
        catchError((err) => {
          console.error('[loadUserData] Error:', err);
          return of(null);
        }),
      );
    });
  }
  // preloadEssentialData(): Observable<any> {
  //   return forkJoin([this.loadConfigurationsData(), this.loadSubscriptionsData(), this.loadMenuItemsData()]).pipe(
  //     catchError(() => {
  //       return of(null);
  //     }),
  //   );
  // }

  // loadUserData(): Observable<any> {
  //   const currentMonth = moment().format('YYYY-MM');
  //   return forkJoin([
  //     this.loadProfileData(),
  //     this.loadMySubscriptionsNewData(),
  //     this.loadSubDataData(),
  //     this.loadAddressesData(),
  //     this.loadCalendarData(currentMonth),
  //     this.loadConsultationsData(),
  //     this.loadConsultantsData(this.today),
  //     this.loadCMSPagesData(),
  //   ]).pipe(
  //     catchError(() => {
  //       return of(null);
  //     }),
  //   );
  // }

  public getData(key: string, params?: any, forceRefresh: boolean = false): Observable<any> {
    if (forceRefresh) {
      const url = this.getUrlForKey(key, params);
      if (url && this.cachedObservables.has(url)) {
        this.cachedObservables.delete(url);
      }
      if (this.cachedData.has(key)) {
        this.cachedData.delete(key);
      }
    }

    switch (key) {
      case 'slider':
        return this.loadSliderData();
              case 'areas':
        return this.loadAreasData();
      case 'pages':
        return this.loadCMSPagesData();
      case 'configurations':
        return this.loadConfigurationsData();
      case 'profile':
        return this.loadProfileData();
      case 'subscriptions':
        return this.loadSubscriptionsData();
      case 'mysubscriptionsnew':
        return this.loadMySubscriptionsNewData();
      case 'subdata':
        return this.loadSubDataData();
      case 'calendar':
        const month = params?.month || moment().format('YYYY-MM');
        return this.loadCalendarData(month);
      case 'addresses':
        return this.loadAddressesData();
      case 'menuItems':
        return this.loadMenuItemsData();
      case 'slots':
        const areaId = params?.area_id || 1;
        return this.loadSlotData(areaId);
      case 'driverNote':
        return this.loadDriverNoteData();
      case 'consultations':
        return this.loadConsultationsData();
      case 'consultants':
        const bookDate = params?.date || this.today;
        return this.loadConsultantsData(bookDate);
      case 'menuList':
        return this.loadMenuPageData();
              case 'menuWithDate':
        const date = params?.date;
        return this.loadMenuWithDateData(date);
      default:
        return of(null);
    }
  }

  private getUrlForKey(key: string, params?: any): string | null {
    const user = this._currentUser$.getValue();
    const customerId = user?.id;
    const areaId = params?.area_id || 1;
    const month = params?.month || moment(this.today).format('YYYY-MM');
    const date = params?.date || this.today;
    const doubleBox = params?.doubleBox || false;
    switch (key) {
      case 'slider':        
        return endpoints.slider;
              case 'areas':        
        return endpoints.areas;
      case 'configurations':
        return endpoints.configuration;
      case 'profile':
        return customerId ? `${endpoints.profile}?customer_id=${customerId}` : null;
      case 'subscriptions':
        return endpoints.subscriptions;
      case 'mysubscriptionsnew':
        return customerId ? `${endpoints.mySubscriptionsnew}?customer_id=${customerId}` : null;
      case 'subdata':
        return customerId ? `${endpoints.subData}?customer_id=${customerId}` : null;
      case 'calendar':
        return customerId ? `${endpoints.calendar}?customer_id=${customerId}&month=${month}` : null;
      case 'addresses':
        return customerId ? `${endpoints.addresses}?customer_id=${customerId}` : null;
      case 'menuItems':
        return endpoints.menuItems;
      case 'slots':
        return `${endpoints.slots}?area_id=${areaId}`;
      case 'driverNote':
        return customerId ? `${endpoints.driver_notes}?customer_id=${customerId}` : null;
      case 'consultations':
        return customerId ? `${endpoints.consultations}?customer_id=${customerId}` : null;
              case 'consultants':
        return customerId ? `${endpoints.consultants}?customer_id=${customerId}&date=${date}` : null;
      case 'menuWithDate':
        return customerId && date ? `${endpoints.menus}?customer_id=${customerId}&date=${date}&double_box=${doubleBox}` : null;
              case 'menuList':
        return customerId && date ? `${endpoints.menus}?customer_id=${customerId}&date=${date}&double_box=${doubleBox}` : null;
      default:
        return null;
    }
  }

  clearUserSpecificData(): void {
    this.cachedObservables.forEach((_value, key) => {
      if (key.includes('customer_id')) {
        this.cachedObservables.delete(key);
      }
    });
  }

  showData(params?) {
    console.log(this.cachedObservables, 'masterLoad');
    const areaId = params?.area_id || 1;
    const month = params?.month || moment(this.today).format('YYYY-MM');
    const date = params?.date || this.today;
    const doubleBox = params?.doubleBox || false;
    this.cachedObservables.forEach((_value, key) => {
      // const url = this.getUrlForKey(key);
      this.getCachedData(key);
    });
    const allData = Array.from(this.cachedData.entries()).map(([url, data]) => ({
      url,
      data,
    }));
    console.log(allData, 'all cached data');
    return allData;
  }

  getCachedData(key: string, params?: any, force: boolean = false): any | null {
    const url = this.getUrlForKey(key, params);
    let cache = this.cachedData.get(url) ?? null;
    if (cache == null || force) {
      this.getData(key, params, true).subscribe((res) => {
        cache = res;
        return cache;
      });
    } else {
      return cache;
    }
  }

  clearAllData() {
    // this.clearUserSpecificData();
    this.cachedObservables.forEach((_value, key) => {
      // if (key.includes('customer_id')) {
      this.cachedObservables.delete(key);
      // }
    });
    this.cachedData.clear();
    // this.cachedData.forEach((key) => {
    //   this.cachedData.delete(key);
    // });

    console.log(this.cachedData, this.cachedObservables, 'is Data clear');
  }
}
/*
// Basic Observable Call - 

this.dataService.getData('pages').subscribe(res => {
  console.log('async data', res);
});

// sync access (after first load)
const pagesData = this.dataService.getCachedData('pages');
console.log('sync data', pagesData);

// all cached at once
const allCached = this.dataService.showData();
console.log(allCached);

this.dataService.getData('profile').subscribe(data => {
  console.log('Raw API data', data);
});

// Save Transformed Version - 

this.dataService.getData('profile').subscribe(raw => {
  const transformed = { ...raw, fullName: raw.first_name + ' ' + raw.last_name };
  this.dataService.setTransformedData('profile_transformed', transformed);
});

// Access saved transformed version from anywhere - 

this.dataService.getTransformedData('profile_transformed').subscribe(transformed => {
  console.log('Transformed profile', transformed);
});

// Syncronized Data - if sure data exists can use this - 

const cached = this.dataService.getTransformedSnapshot('profile_transformed');
console.log(cached);
*/
